import React, { forwardRef, ReactNode } from 'react';

import { useMUITheme } from '../styles/theme';
import Select, { SelectProps } from '../Select';
import ListSubheader from '../ListSubheader';
import MenuItem from '../MenuItem';
import Typography from '../Typography';
import { css, cx } from '../../customEmotion';

import { useCustomOptionStyle, useSelectStyle } from './styles';

export type MenuItemOption = {
  value?: string | number;
  children?: string | ReactNode;
  emptyLabel?: string | ReactNode;
};

export type Option = {
  optionType: 'root' | 'var' | 'container';
  children?: string | ReactNode;
  value?: string | number;
};

export type MenuGroups = {
  optionType: 'container' | 'root' | 'var';
  groupTitle: string;
  options: MenuItemOption[];
};

interface VarSelectProps extends SelectProps {
  /**
   * Data structure required by VarSelect
   */
  groups?: MenuGroups[];
  options?: Option[];
  disabledOptions?: Array<Option['value']>;
}

const VarSelect = forwardRef<HTMLDivElement, VarSelectProps>((props, ref) => {
  const theme = useMUITheme();
  const customOptionStyle = useCustomOptionStyle();
  const selectStyle = useSelectStyle();

  const {
    groups,
    options,
    disabledOptions,
    value,
    onChange,
    variant = 'outlined',
    onOpen,
    onClose,
    ...rest
  } = props;

  return (
    <Select
      {...rest}
      ref={ref}
      value={value}
      onChange={onChange}
      variant={variant}
      onOpen={onOpen}
      onClose={onClose}
      displayEmpty
      fullWidth
      classes={selectStyle}
    >
      {groups &&
        groups.map(group => {
          return [
            <ListSubheader style={{ pointerEvents: 'none' }}>
              {group.groupTitle}
            </ListSubheader>,
            group.options.map(opt => {
              if (opt.emptyLabel) {
                return (
                  <MenuItem style={{ pointerEvents: 'none' }}>
                    <Typography
                      variant="body2"
                      className={css`
                        color: ${theme.palette.grey[500]};
                      `}
                    >
                      {opt.emptyLabel}
                    </Typography>
                  </MenuItem>
                );
              }

              return (
                <MenuItem
                  className={customOptionStyle.container}
                  value={opt.value}
                  key={opt.value}
                >
                  <div
                    className={cx(
                      customOptionStyle.root,
                      customOptionStyle[group.optionType]
                    )}
                  >
                    {opt.children}
                  </div>
                </MenuItem>
              );
            }),
          ];
        })}
      {options &&
        options.map(opt => (
          <MenuItem
            className={customOptionStyle.container}
            value={opt.value}
            key={opt.value}
            disabled={disabledOptions && disabledOptions.includes(opt.value)}
          >
            <div
              className={cx(
                customOptionStyle.root,
                customOptionStyle[opt.optionType]
              )}
            >
              {opt.children}
            </div>
          </MenuItem>
        ))}
    </Select>
  );
});

VarSelect.displayName = 'VarSelect';

export default VarSelect;
